import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { PagePaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { usePlotRequestDetail } from "@/features/map/hooks/usePlotRequestDetail";
import { MapPlotRequestDetailGrid } from "@/features/mapPlotRequest/components/MapPlotRequestDetailGrid";
import { AddPlotDetailModal } from "@/features/mapPlotRequest/components/AddPlotDetailModal";
import { BulkEditPlotDetailsModal } from "@/features/mapPlotRequest/components/BulkEditPlotDetailsModal";
import { DeletePlotDetailsConfirmModal } from "@/features/mapPlotRequest/components/DeletePlotDetailsConfirmModal";
import { MapPlotRequestFilterModal } from "@/features/mapPlotRequest/components/MapPlotRequestFilterModal";
import { MapPlotRequestTagModal } from "@/features/mapPlotRequest/components/MapPlotRequestTagModal";
import { KeyboardArrowLeft } from "@mui/icons-material";
import { exportPlotRequestDetails } from "@/utils/mapPlotRequestExport";
import { useAdditionalFieldsConfig } from "@/features/map/hooks/useAdditionalFieldsConfig";
import {
  type GridSelectionModel,
  type GridCellEditCommitParams,
} from "@mui/x-data-grid-pro";
import {
  type SavedMapPlotRequestDetail,
  type PlotRequestTag,
  type MapPlotRequestDetailAdditionalFields,
  type PlotRequestFilterValues,
} from "@/features/map/types";
import { useBulkUpdatePlotRequestDetails } from "@/features/map/hooks/useBulkUpdatePlotRequestDetails";
import { toast } from "react-toastify";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useMapPlotRequest } from "@/features/map/hooks/useMapPlotRequest";
import { useFilteredPlotDetails } from "@/features/mapPlotRequest/hooks/useFilteredPlotDetails";
import { PlotRequestHeader } from "@/features/mapPlotRequest/components/PlotRequestHeader";
import { PlotRequestActions } from "@/features/mapPlotRequest/components/PlotRequestActions";
import { PlotRequestFilterChips } from "@/features/mapPlotRequest/components/PlotRequestFilterChips";
import { usePlotRequestEditor } from "@/features/mapPlotRequest/hooks/usePlotRequestEditor";
import { usePlotDetailModals } from "@/features/mapPlotRequest/hooks/usePlotDetailModals";
import { usePrefectures } from "@/hooks/usePrefectures";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { convertToAcquireMultipleBookData } from "@/features/mapPlotRequest/utils/convertToAcquireMultipleBookData";
import { parseAdditionalFields } from "@/features/map/utils/additionalFieldsHelpers";
import type { SavedMapPlotDataListWithRequestId } from "@/features/map/types/component";

const ID_PATTERN = /^[0-9]+$/;

const parseId = (value: string | undefined): number | undefined => {
  if (!value || !ID_PATTERN.test(value)) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
};

/**
 * 地図プロットリクエスト詳細画面コンポーネント
 *
 * プロットリクエストの詳細情報を表示し、以下の機能を提供します：
 * - プロット詳細データの一覧表示（DataGrid）
 * - フィルター機能（作成日時、更新日時、タグ）
 * - 地図プロットリクエストタグの編集
 * - 行の追加・一括編集
 * - 各行のタグ・備考の編集
 */
export const MapPlotRequestDetail: React.FC = () => {
  const { id } = useParams();
  // カスタム（追加）項目の設定情報を取得
  const { availableFields } = useAdditionalFieldsConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { mapPlotRequestDetailBulkEdit, crm: crmFeatureFlag } =
    useFeatureFlags();
  const crm = Boolean(crmFeatureFlag);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);
  const [filters, setFilters] = useState<PlotRequestFilterValues>({
    createdAtFrom: null,
    createdAtTo: null,
    updatedAtFrom: null,
    updatedAtTo: null,
    tags: [],
    status: [],
    type: [],
    requestAddress: null,
    requestLocation: null,
    requestChiban: null,
    requestKaokuNumber: null,
    remarks: null,
  });
  const [plotErrorMessage, setPlotErrorMessage] = useState<string | null>(null);
  const [mapPlotRequestTagOptions, setMapPlotRequestTagOptions] = useState<
    string[]
  >([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const { prefectures } = usePrefectures();
  const plotRequestId = parseId(id);

  // モーダルから遷移してきた場合（shouldRefresh=true）は初回マウント時の取得をスキップ
  // 代わりにuseEffectでmutate()を呼ぶため、重複を避ける
  const shouldSkipMountFetch = React.useMemo(() => {
    const state = location.state as { shouldRefresh?: boolean } | null;
    return state?.shouldRefresh === true;
  }, [location.state]);

  const { data, isLoading, isError, mutate } = usePlotRequestDetail(
    plotRequestId,
    {
      revalidateOnMount: !shouldSkipMountFetch,
    }
  );

  // プロット一覧から地図プロットリクエストタグ候補を取得
  const { refetch: fetchPlotList } = useMapPlotRequest(false);

  // プロット詳細の一括更新フック
  const { trigger: bulkUpdateDetails } = useBulkUpdatePlotRequestDetails();

  const {
    isEditingName,
    editedName,
    setEditedName,
    handleStartEditingName,
    handleCancelEditingName,
    handleNameSave,
    isEditingMapPlotRequestTags,
    control,
    handleStartEditingMapPlotRequestTags,
    handleCancelEditingMapPlotRequestTags,
    handleMapPlotRequestTagsSave,
  } = usePlotRequestEditor(
    plotRequestId,
    data?.name ?? "",
    data?.tags ?? [],
    async () => {
      await mutate();
    }
  );

  // カスタムフック: モーダルの開閉状態管理
  const {
    openAddModal,
    setOpenAddModal,
    openBulkEditModal,
    setOpenBulkEditModal,
    openDeleteConfirmModal,
    setOpenDeleteConfirmModal,
    openFilterModal,
    setOpenFilterModal,
    openTagModal,
    selectedRowForEdit,
    handleOpenAddModal,
    handleOpenBulkEditModal,
    handleOpenDeleteConfirmModal,
    handleOpenFilterModal,
    handleOpenTagModal,
    handleCloseTagModal,
  } = usePlotDetailModals();

  const handleOpenFilesPage = (detail: SavedMapPlotRequestDetail): void => {
    if (plotRequestId === undefined) {
      return;
    }

    navigate(`/mapplotrequest/${plotRequestId}/details/${detail.id}/files`);
  };

  /**
   * MapPlotRequestのタグ選択肢を取得
   * モーダル経由ではなく直接詳細ページに遷移した時はAPIで取得
   */
  useEffect(() => {
    const loadMapPlotRequestTags = async (): Promise<void> => {
      // location.stateからタグ選択肢を取得
      const state = location.state as {
        mapPlotRequestTagOptions?: string[];
      } | null;
      if (
        state?.mapPlotRequestTagOptions &&
        state.mapPlotRequestTagOptions.length > 0
      ) {
        setMapPlotRequestTagOptions(state.mapPlotRequestTagOptions);
        return;
      }

      try {
        const plotList = await fetchPlotList();
        // プロット一覧からタグを抽出して重複排除
        const allTags: string[] = [];
        plotList.forEach((plot) => {
          if (plot.tags && Array.isArray(plot.tags)) {
            plot.tags.forEach((tag: PlotRequestTag) => {
              allTags.push(tag.name);
            });
          }
        });
        const uniqueTags = Array.from(new Set(allTags));
        setMapPlotRequestTagOptions(uniqueTags);
      } catch (error) {
        console.error(
          "地図プロットリクエストタグ選択肢の取得に失敗しました:",
          error
        );
      }
    };
    void loadMapPlotRequestTags();
  }, [fetchPlotList, location.state]);

  /**
   * DataGridの選択行を更新
   */
  const handleSelectionChange = (newSelection: GridSelectionModel): void => {
    setSelectedIds(newSelection);
    // 選択が変更されたらエラーメッセージをクリア
    setPlotErrorMessage(null);
  };

  /**
   * データを再取得
   */
  const handleRefreshData = (): void => {
    void mutate();
  };

  /**
   * 地図画面のモーダル（SavedPlotModal）から当ページに遷移した際のデータ再取得処理
   *
   * 背景:
   * - 地図画面でピンの備考等を編集後、モーダルの「詳細」リンクから当ページに遷移するケースがある
   * - この場合、編集内容を反映させるため、データの再取得が必要
   *
   * 実装:
   * - SavedPlotFeedGridから遷移時に location.state.shouldRefresh = true が渡される
   * - shouldRefresh = true の場合、usePlotRequestDetailに revalidateOnMount: false を渡してマウント時の自動取得をスキップ
   * - 代わりにこのuseEffectでmutate()を実行してAPIから最新データを取得（1回のみ）
   * - location.keyをチェックすることで、新しい遷移があった時のみ処理を実行
   * - navigate()でshouldRefreshをfalseに更新しても、location.keyは変わらないため再実行されない
   */
  const previousLocationKey = React.useRef<string | undefined>(undefined);
  useEffect(() => {
    // location.keyが変わった時のみ処理（新しい遷移があった時）
    if (previousLocationKey.current !== location.key) {
      previousLocationKey.current = location.key;

      const state = location.state as { shouldRefresh?: boolean } | null;
      if (state?.shouldRefresh) {
        // 最新データを取得
        void mutate();

        // shouldRefreshをfalseに更新してブラウザの履歴に記録
        // これにより、ユーザーがブラウザの「戻る」ボタンで戻ってきた時や
        // ページをリロードした時に、再度mutate()が実行されることを防ぐ
        // replace: true なので履歴は置き換えられ、location.keyは変わらない
        navigate(location.pathname, {
          replace: true,
          state: { ...state, shouldRefresh: false },
        });
      }
    }
  }, [location.key, location.state, location.pathname, mutate, navigate]);

  /**
   * 選択中の詳細データを取得
   */
  const getSelectedDetails = useCallback((): SavedMapPlotRequestDetail[] => {
    if (!data) return [];
    return data.details.filter((detail) => selectedIds.includes(detail.id));
  }, [data, selectedIds]);

  /**
   * プロット可能な件数を計算
   * 選択がある場合は選択した成功データの件数、ない場合は全ての成功データの件数
   */
  const availablePlotCount = useMemo((): number => {
    if (!data) return 0;

    // 選択がある場合は選択した成功データ（土地のみ）の件数を返す
    if (selectedIds.length > 0) {
      const selectedDetails = getSelectedDetails();
      return selectedDetails.filter(
        (detail) =>
          detail.status === "SUCCEED" &&
          detail.substituteLatitude !== null &&
          detail.substituteLongitude !== null &&
          detail.realEstateType === "土地"
      ).length;
    }

    // 選択がない場合は全ての成功データ（土地のみ）の件数を返す
    return data.details.filter(
      (detail) =>
        detail.status === "SUCCEED" &&
        detail.substituteLatitude !== null &&
        detail.substituteLongitude !== null &&
        detail.realEstateType === "土地"
    ).length;
  }, [data, selectedIds, getSelectedDetails]);

  /**
   * プロット詳細で使用されているタグを取得
   */
  const usedTags = useMemo((): PlotRequestTag[] => {
    if (!data) return [];

    const tagMap = new Map<string, PlotRequestTag>();
    data.details.forEach((detail) => {
      detail.tags.forEach((tag) => {
        tagMap.set(tag.name, tag);
      });
    });

    return Array.from(tagMap.values());
  }, [data]);

  /**
   * フィルター条件を適用した詳細データを取得
   */
  const filteredDetails = useFilteredPlotDetails(data, filters);

  /**
   * パフォーマンス最適化: additionalFieldsを事前にパースして各行に持たせる
   */
  const processedDetails = useMemo(() => {
    return filteredDetails.map((detail) => ({
      ...detail,
      _parsedAdditionalFields: parseAdditionalFields(detail.additionalFields),
    }));
  }, [filteredDetails]);

  /**
   * フィルター条件を適用
   */
  const handleApplyFilters = (newFilters: PlotRequestFilterValues): void => {
    setFilters(newFilters);
  };

  /**
   * すべてのフィルター条件をクリア
   */
  const handleClearFilters = (): void => {
    setFilters({
      createdAtFrom: null,
      createdAtTo: null,
      updatedAtFrom: null,
      updatedAtTo: null,
      tags: [],
      status: [],
      type: [],
      requestAddress: null,
      requestLocation: null,
      requestChiban: null,
      requestKaokuNumber: null,
      remarks: null,
    });
  };

  /**
   * 詳細行のタグを保存
   * @param id - 詳細データのID
   * @param tags - 保存するタグ名の配列
   */
  const handleTagSave = async (id: number, tags: string[]): Promise<void> => {
    if (isUpdating) return;

    const tagNames = tags;

    const updatePayload = {
      id,
      tags: tagNames,
    };

    setIsUpdating(true);
    let errorOccurred = false;

    await bulkUpdateDetails(
      {
        details: [updatePayload],
      },
      {
        throwOnError: false,
        onError: () => {
          errorOccurred = true;
          toast.error("タグの更新に失敗しました");
        },
      }
    );

    if (!errorOccurred) {
      toast.success("タグを更新しました");
      await mutate();
    }

    setIsUpdating(false);
  };

  /**
   * 戻るボタンの処理
   * 地図画面に戻り、モーダルを自動オープン
   */
  const handleBack = (): void => {
    navigate("/map", { state: { openSavedPlotModal: true } });
  };

  /**
   * プロットするボタンの処理
   * SavedPlotModal経由で地図にプロットする
   * 選択がない場合は全ての成功データをプロット、選択がある場合は選択した成功データのみをプロット
   * ※建物・区分建物は除外し、土地のみ(経緯: https://trustart-group.slack.com/archives/C04HA6M7SMB/p1763444573320059?thread_ts=1763442229.347609&cid=C04HA6M7SMB)
   */
  const handlePlotToMap = (): void => {
    if (!data || plotRequestId === undefined) return;

    let successDetails: SavedMapPlotRequestDetail[];

    // 選択がない場合は全ての成功データを取得
    if (selectedIds.length === 0) {
      successDetails = data.details.filter(
        (detail) =>
          detail.status === "SUCCEED" &&
          detail.substituteLatitude !== null &&
          detail.substituteLongitude !== null &&
          detail.realEstateType === "土地"
      );
    } else {
      // 選択がある場合は選択された成功データのみを取得(エラー行を除外)
      const selectedDetails = getSelectedDetails();
      successDetails = selectedDetails.filter(
        (detail) =>
          detail.status === "SUCCEED" &&
          detail.substituteLatitude !== null &&
          detail.substituteLongitude !== null &&
          detail.realEstateType === "土地"
      );
    }

    if (successDetails.length === 0) {
      setPlotErrorMessage("プロット可能なデータがありません。");
      return;
    }

    // エラーメッセージをクリア
    setPlotErrorMessage(null);

    const plotDetailsToMap: SavedMapPlotDataListWithRequestId =
      successDetails.map((detail) => ({
        ...detail,
        plotRequestId,
      }));

    // 地図画面に遷移し、プロットデータをSavedPlotModalと同じ形式で渡す
    navigate("/map", {
      state: {
        plotDetailsToMap,
        datasetInfo: data
          ? [{ id: plotRequestId, name: data.name }]
          : undefined,
      },
    });
  };

  /**
   * 登記を取得ボタンのハンドラー
   * 選択された行を複数取得画面に遷移
   */
  const handleAcquireBooks = (): void => {
    if (!data) return;

    const selectedDetails = getSelectedDetails();
    const handoffData = convertToAcquireMultipleBookData(
      selectedDetails,
      prefectures
    );

    if (handoffData.length === 0) {
      setPlotErrorMessage("所在または地番が設定されていない行があります");
      return;
    }

    // エラーメッセージをクリア
    setPlotErrorMessage(null);

    // 複数取得画面に遷移
    navigate("/acquiremultiplebooks", {
      state: handoffData,
    });
  };

  /**
   * 表示中または選択中の物件リストをCSVまたはExcel形式でダウンロードする共通処理
   * @param formatType - ダウンロードするファイル形式 ('csv' | 'excel')
   */
  const handleDownload = (formatType: "csv" | "excel"): void => {
    if (!data) return;

    // ユーザーが明示的にチェックボックスで行を選択している場合はその選択行のみを、
    // 選択していない場合は、現在画面上でフィルターされている全行（processedDetails）をエクスポートの対象とする
    const exportDetails =
      selectedIds.length > 0
        ? processedDetails.filter((detail) => selectedIds.includes(detail.id))
        : processedDetails;

    if (exportDetails.length === 0) {
      toast.warn("ダウンロード対象のデータがありません。");
      return;
    }

    // 共通エクスポートユーティリティを呼び出し、ダウンロードを実行
    exportPlotRequestDetails(
      formatType,
      data.name,
      exportDetails,
      availableFields
    );
  };

  /**
   * DataGridのセル編集を処理
   * @param params - セル編集のパラメータ
   */
  const handleCellEdit = async (
    params: GridCellEditCommitParams
  ): Promise<void> => {
    if (isUpdating) return;

    const updatePayload = {
      id: params.id as number,
      [params.field]: params.value as string | number | null,
    };

    setIsUpdating(true);
    let errorOccurred = false;

    await bulkUpdateDetails(
      {
        details: [updatePayload],
      },
      {
        throwOnError: false,
        onError: () => {
          errorOccurred = true;
          toast.error("更新に失敗しました");
        },
      }
    );

    if (!errorOccurred) {
      toast.success("更新しました");
      await mutate();
    }

    setIsUpdating(false);
  };

  /**
   * 緯度経度の編集を処理
   * @param id - 詳細データのID
   * @param latitude - 新しい緯度（nullの場合は削除）
   * @param longitude - 新しい経度（nullの場合は削除）
   */
  const handleCoordinatesEdit = async (
    id: number,
    latitude: number | null,
    longitude: number | null
  ): Promise<void> => {
    if (isUpdating) return;

    const updatePayload = {
      id,
      substituteLatitude: latitude,
      substituteLongitude: longitude,
    };

    setIsUpdating(true);
    let errorOccurred = false;

    await bulkUpdateDetails(
      {
        details: [updatePayload],
      },
      {
        throwOnError: false,
        onError: () => {
          errorOccurred = true;
          toast.error("更新に失敗しました");
        },
      }
    );

    if (!errorOccurred) {
      toast.success("更新しました");
      await mutate();
    }

    setIsUpdating(false);
  };

  /**
   * 追加項目の編集を処理
   * @param id - 詳細データのID
   * @param additionalFields - 更新する追加項目の値。形式: { fieldId: { value: string } } または null
   */
  const handleAdditionalFieldsEdit = async (
    id: number,
    additionalFields: MapPlotRequestDetailAdditionalFields | null
  ): Promise<void> => {
    if (isUpdating) return;

    const updatePayload = {
      id,
      additionalFields,
    };

    setIsUpdating(true);
    let errorOccurred = false;

    await bulkUpdateDetails(
      {
        details: [updatePayload],
      },
      {
        throwOnError: false,
        onError: () => {
          errorOccurred = true;
          toast.error("更新に失敗しました");
        },
      }
    );

    if (!errorOccurred) {
      toast.success("更新しました");
      await mutate();
    }

    setIsUpdating(false);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (plotRequestId === undefined) {
    return (
      <PagePaper>
        <Stack alignItems="center" spacing={2}>
          <Typography color="error">URLが不正です。</Typography>
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowLeft />}
            onClick={() => {
              navigate("/map", { state: { openSavedPlotModal: true } });
            }}
          >
            戻る
          </Button>
        </Stack>
      </PagePaper>
    );
  }

  if (isError || !data) {
    return (
      <PagePaper>
        <Stack alignItems="center" spacing={2}>
          <Typography color="error">データの取得に失敗しました。</Typography>
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowLeft />}
            onClick={() => {
              navigate(-1);
            }}
          >
            戻る
          </Button>
        </Stack>
      </PagePaper>
    );
  }

  return (
    <PagePaper>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            variant="text"
            startIcon={<KeyboardArrowLeft />}
            onClick={handleBack}
          >
            戻る
          </Button>
        </Box>
        <PageTitle>{crm ? "物件リスト詳細" : "プロット情報詳細"}</PageTitle>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Table size="small" sx={{ mb: 2 }}>
            <TableBody>
              <TableRow>
                <TableCell
                  component="th"
                  sx={{
                    width: "10%",
                    border: "none",
                    p: 1,
                    pl: 0,
                    pr: 2,
                  }}
                >
                  件数
                </TableCell>
                <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                  {formatNumberWithComma(data.details.length)}件（成功：
                  {formatNumberWithComma(
                    data.details.filter((d) => d.status === "SUCCEED").length
                  )}
                  件、失敗：
                  {formatNumberWithComma(
                    data.details.filter((d) => d.status === "FAILED").length
                  )}
                  件）
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
      </Grid>

      <PlotRequestHeader
        name={data.name}
        tags={data.tags}
        isEditingName={isEditingName}
        editedName={editedName}
        isEditingTags={isEditingMapPlotRequestTags}
        mapPlotRequestTagOptions={mapPlotRequestTagOptions}
        control={control}
        onStartEditingName={handleStartEditingName}
        onCancelEditingName={handleCancelEditingName}
        onNameChange={setEditedName}
        onNameSave={() => {
          void handleNameSave();
        }}
        onStartEditingTags={handleStartEditingMapPlotRequestTags}
        onCancelEditingTags={handleCancelEditingMapPlotRequestTags}
        onTagsSave={() => {
          void handleMapPlotRequestTagsSave();
        }}
      />

      <PlotRequestActions
        selectedCount={selectedIds.length}
        availablePlotCount={availablePlotCount}
        bulkEditEnabled={Boolean(mapPlotRequestDetailBulkEdit)}
        onAddRow={handleOpenAddModal}
        onBulkEdit={handleOpenBulkEditModal}
        onFilter={handleOpenFilterModal}
        onPlotToMap={handlePlotToMap}
        onDelete={handleOpenDeleteConfirmModal}
        onAcquireBooks={crm ? handleAcquireBooks : undefined}
        onDownloadCSV={() => {
          handleDownload("csv");
        }}
        onDownloadExcel={() => {
          handleDownload("excel");
        }}
        downloadDisabled={processedDetails.length === 0}
      />

      {plotErrorMessage && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="error">
            {plotErrorMessage}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          mb: 2,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <PlotRequestFilterChips
          filters={filters}
          onFilterChange={setFilters}
          onClearAll={handleClearFilters}
        />
      </Box>

      <Box sx={{ width: "100%", height: 600 }}>
        <MapPlotRequestDetailGrid
          plotRequestId={plotRequestId}
          details={processedDetails}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          onCellEdit={(params) => {
            void handleCellEdit(params);
          }}
          onCoordinatesEdit={(id, latitude, longitude) => {
            void handleCoordinatesEdit(id, latitude, longitude);
          }}
          availableTags={data.tags}
          onTagEdit={(id, tags) => {
            handleOpenTagModal(id, tags, data.details);
          }}
          onOpenFilesPage={handleOpenFilesPage}
          onAdditionalFieldsEdit={(id, additionalFields) => {
            void handleAdditionalFieldsEdit(id, additionalFields);
          }}
        />
      </Box>

      <AddPlotDetailModal
        open={openAddModal}
        setOpen={setOpenAddModal}
        plotRequestId={plotRequestId}
        availableTags={usedTags}
        onSuccess={handleRefreshData}
      />

      <BulkEditPlotDetailsModal
        open={openBulkEditModal}
        setOpen={setOpenBulkEditModal}
        selectedDetails={getSelectedDetails()}
        availableTags={usedTags}
        onSuccess={() => {
          handleRefreshData();
          setSelectedIds([]);
        }}
      />

      <MapPlotRequestFilterModal
        open={openFilterModal}
        onClose={() => {
          setOpenFilterModal(false);
        }}
        availableTags={usedTags}
        initialFilters={filters}
        onApply={handleApplyFilters}
      />

      <MapPlotRequestTagModal
        open={openTagModal}
        onClose={handleCloseTagModal}
        rowData={selectedRowForEdit}
        onSave={(id, tags) => {
          void handleTagSave(id, tags);
        }}
      />

      <DeletePlotDetailsConfirmModal
        open={openDeleteConfirmModal}
        setOpen={setOpenDeleteConfirmModal}
        selectedIds={selectedIds as number[]}
        totalCount={data?.details.length ?? 0}
        onSuccess={() => {
          handleRefreshData();
          setSelectedIds([]);
        }}
      />
    </PagePaper>
  );
};
